<template>
  <teleport to="body">
    <div class="support-contact">
      <div class="modal fade" id="supportContactModal" tabindex="-1" aria-labelledby="supportContactLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
            <div class="modal-header border-bottom-0 cute-header shadow-lg">
              <p class="modal-title text-white text-xxs mb-0" id="supportContactLabel">Support Contact</p>
              <button
                type="button"
                class="modal-btn-close btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <div class="modal-body p-3">
              <p class="fw-bold text-xxs mb-3">
                Update the contact details shown to users when they need help.
              </p>

              <div class="modal-form-group form-group mb-3">
                <label class="modal-form-label form-label text-xxs" for="support_contact_email">
                  Email address
                </label>
                <input
                  id="support_contact_email"
                  v-model="form.contact_email"
                  type="email"
                  class="modal-form-control form-control text-xxs rounded-small shadow-sm"
                  placeholder="you@example.com"
                />
                <small v-if="errors.email" class="text-danger text-xxs fw-bold">{{ errors.email }}</small>
              </div>

              <div class="modal-form-group form-group mb-3">
                <label class="modal-form-label form-label text-xxs" for="support_contact_phone">
                  Contact number
                </label>
                <input
                  id="support_contact_phone"
                  v-model="form.contact_phone"
                  type="tel"
                  class="modal-form-control form-control text-xxs rounded-small shadow-sm"
                  placeholder="+27 12 345 6789"
                />
                <small v-if="errors.phone" class="text-danger text-xxs fw-bold">{{ errors.phone }}</small>
              </div>

              <div v-if="status_message" :class="['alert', status_type === 'success' ? 'alert-success' : 'alert-danger', 'text-xxs']" role="alert">
                {{ status_message }}
              </div>
            </div>

            <div class="modal-footer border-0 p-2 d-flex justify-content-end">
              <button
                @click="save_support_contact"
                :disabled="is_saving"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              >
                <span v-if="!is_saving">Save</span>
                <span v-else>
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import {
  GET_SUPPORT_CONTACT_URL,
  SAVE_SUPPORT_CONTACT_URL,
  get_headers
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

export default {
  name: 'SupportContact',
  mixins: [alert_mixin],
  data() {
    return {
      form: {
        contact_email: '',
        contact_phone: ''
      },
      errors: {
        email: '',
        phone: ''
      },
      is_saving: false,
      status_message: '',
      status_type: 'success',
      status_message_timeout_id: null,
      headers: {}
    };
  },
  methods: {
    clear_status_message_timeout() {
      if (this.status_message_timeout_id) {
        clearTimeout(this.status_message_timeout_id);
        this.status_message_timeout_id = null;
      }
    },

    schedule_status_message_clear(delay = 5000) {
      this.clear_status_message_timeout();
      this.status_message_timeout_id = setTimeout(() => {
        this.status_message = '';
        this.status_message_timeout_id = null;
      }, delay);
    },

    async load_support_contact() {
      try {
        const response = await axios.get(GET_SUPPORT_CONTACT_URL, { headers: this.headers });
        const data = response?.data?.data || {};
        this.form.contact_email = data.contact_email || '';
        this.form.contact_phone = data.contact_phone || '';
      } catch (error) {
        console.warn('Could not load support contact:', error);
      }
    },

    validate_form() {
      this.errors = { email: '', phone: '' };
      let is_valid = true;

      if (!this.form.contact_email || !this.form.contact_email.trim()) {
        this.errors.email = 'Email address is required';
        is_valid = false;
      } else if (!this.is_valid_email(this.form.contact_email)) {
        this.errors.email = 'Please enter a valid email address';
        is_valid = false;
      }

      if (!this.form.contact_phone || !this.form.contact_phone.trim()) {
        this.errors.phone = 'Contact number is required';
        is_valid = false;
      }

      return is_valid;
    },

    is_valid_email(email) {
      const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email_regex.test(email);
    },

    async save_support_contact() {
      if (!this.validate_form()) {
        return;
      }

      this.is_saving = true;
      this.status_message = '';
      this.clear_status_message_timeout();

      try {
        const payload = {
          contact_email: this.form.contact_email.trim(),
          contact_phone: this.form.contact_phone.trim()
        };

        const response = await axios.post(SAVE_SUPPORT_CONTACT_URL, payload, { headers: this.headers });

        if (response?.data?.status === 'success') {
          this.status_message = 'Support contact information saved successfully!';
          this.status_type = 'success';
          this.schedule_status_message_clear(3000);
          setTimeout(() => {
            const modal = document.getElementById('supportContactModal');
            if (modal) {
              const bootstrap_modal = window.bootstrap.Modal.getInstance(modal);
              if (bootstrap_modal) {
                bootstrap_modal.hide();
              }
            }
          }, 1500);
        } else {
          this.status_message = response?.data?.message || 'Failed to save support contact information';
          this.status_type = 'danger';
        }
      } catch (error) {
        this.status_message = error?.response?.data?.message || 'An error occurred while saving support contact information';
        this.status_type = 'danger';
      } finally {
        this.is_saving = false;
      }
    },

    open_support_contact_modal() {
      this.clear_status_message_timeout();
      this.status_message = '';
      this.load_support_contact();
      const modal = new window.bootstrap.Modal(document.getElementById('supportContactModal'));
      modal.show();
    }
  },
  beforeUnmount() {
    this.clear_status_message_timeout();
  },
  mounted() {
    this.headers = get_headers();
  }
};
</script>

<style scoped>
.support-contact .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
